import { useSignal, useSignalEffect } from "@preact/signals-react";
import { INGIFrameProps } from "../../library/NGFieldExtensions";
import { setupHandlers, setupIframe, setupLocalState } from "../../library/dataService";
import "./iframe.css";
import { useRef } from "react";
import { log } from "../../library/logger";
import { getStyleObject, getTestId, getClassName, normalizeSessionUrl, sanitizeHref } from "../../library/utils";

const tag = "iframe";

export function NGIFrame({ config, context }: INGIFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const iframeId = config.Id;
  const divAroundIFrameTestId = `divAroundIFrame_${getTestId(config)}`;

  const local = setupLocalState(
    config,
    {
      Width: useSignal(config.Width || "100%"),
      Height: useSignal(config.Height || "90vh"),
      Value: useSignal(config.Source ?? config.Value ?? ""),
      Style: useSignal(config.Style ?? {}),
      Classes: useSignal(config.Classes ?? []),
      Reload: useSignal(false),
    },
    context
  );

  const handlers = setupHandlers(config, context);

  function handleMessage(message) {
    const msg = message.data;
    if (msg.Source != "ng") return;
    log.debug(tag, "handleMessage", msg, window.location.href);

    if (handlers[msg.Type]) {
      log.debug(tag, "handleMessage", "got handler message: ", msg, window.location.href);
      handlers[msg.Type](msg, msg);
    }
  }

  useSignalEffect(() => {
    log.debug(tag, "useSignalEffect", iframeRef.current?.ContentWindow, window.location.href);

    // init the iframe once
    setupIframe(config.Id, iframeRef.current);

    window.addEventListener("message", handleMessage);
  });

  useSignalEffect(() => {
    if (!local.Reload.value || !iframeRef.current) return;
    log.debug(tag, "useSignalEffect", "reload iframe", window.location.href);
    iframeRef.current.src = normalizeSessionUrl(local.Value.value);
    local.Reload.value = false;
  });

  const iframeStyle = {
    width: local.Width.value,
    height: local.Height.value,
  };

  const className = getClassName(local.Classes);
  const safeSrc = sanitizeHref(normalizeSessionUrl(local.Value.value));

  return (
    <>
      <div style={{ textAlign: "center" }} data-testid={divAroundIFrameTestId}>
        <iframe
          data-testid={getTestId(config)}
          data-type={config.__typename}
          id={iframeId}
          ref={iframeRef}
          style={getStyleObject(local.Style.value, iframeStyle)}
          className={className ? `${className} iframe` : "iframe"}
          frameborder={config.FrameBorder ?? ""}
          allow={config.Allow ?? ""}
          allowfullscreen={config.AllowFullscreen ?? false}
          {...(safeSrc ? { src: safeSrc } : {})}
        />
      </div>
    </>
  );
}
